
import { defineComponent } from '@vue/composition-api';
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment-timezone';
import { MyBet, MyBetsFilters, MyBetsStats } from '../../../../../types/sport';
import BetItem from './BetItem.vue';
import BetFilters from './BetFilters.vue';
import BetStats from './BetStats.vue';
import BetStatusIcon from './BetStatusIcon.vue';
import BetDetailModal from './BetDetailModal.vue';
import IllinoisIcons from '../icons.vue';

export default defineComponent({
  name: 'BetListModal',
  components: {
    BetItem,
    BetFilters,
    BetStats,
    BetStatusIcon,
    BetDetailModal,
    IllinoisIcons
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const now = new Date();
    const oneMonthAgo = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate()
    );

    return {
      loading: false,
      currentPage: 1,
      itemsPerPage: 30,
      showDetailModal: false,
      selectedBet: null as MyBet | null,
      detailLoading: false,
      cashout: 0,
      cashoutPrematch: 0,
      filters: {
        type: '',
        result: '',
        payed: '',
        code: '',
        betCode: '',
        bet_context: '',
        fromTime: oneMonthAgo.toISOString().substr(0, 10),
        toTime: new Date().toISOString().substr(0, 10),
        onlyPlayedByMe: true
      } as MyBetsFilters,
      betsStats: {
        total: 0,
        totalPlayed: 0,
        winner: 0,
        void: 0,
        pending: 0,
        net: 0,
        currency: ''
      } as MyBetsStats
    };
  },
  computed: {
    ...mapGetters({
      getMyBetsAll: 'user/getMyBetsAll',
      getShortCode: 'settings/getShortCode',
      getUserType: 'user/getType',
      getCashier: 'user/getCashier',
      getPlatformSettings: 'settings/getPlatformSettings',
      getRulesManager: 'rules/getRulesManager',
      getRulesSkin: 'rules/getRulesSkin',
      getCashout: 'settings/getCashout',
      getCashoutLive: 'settings/getCashoutLive',
      getDetailMyBets: 'user/getDetailMyBets'
    }),
    myBetsAll(): MyBet[] {
      return this.getMyBetsAll || [];
    },
    showBetCode(): boolean {
      return this.getShortCode || false;
    },
    showOnlyMyBetsToggle(): boolean {
      return (
        (this.getUserType != null && this.getUserType === 4) ||
        (this.getCashier != null && this.getCashier === true)
      );
    },
    paginatedBets(): MyBet[] {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.myBetsAll.slice(start, end);
    },
    totalPages(): number {
      return Math.ceil(this.myBetsAll.length / this.itemsPerPage);
    },
    paginationStart(): number {
      return this.myBetsAll.length === 0
        ? 0
        : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    paginationEnd(): number {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.myBetsAll.length ? this.myBetsAll.length : end;
    },
    visiblePages(): number[] {
      const pages: number[] = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const maxVisible = 5;

      if (total <= maxVisible) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 3) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
        } else if (current >= total - 2) {
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          for (let i = current - 2; i <= current + 2; i++) {
            pages.push(i);
          }
        }
      }

      return pages;
    }
  },
  watch: {
    visible: {
      async handler(newValue: boolean) {
        if (newValue) {
          await this.initialize();
        } else {
          this.currentPage = 1;
          this.showDetailModal = false;
          this.selectedBet = null;
        }
      },
      immediate: false
    },
    getDetailMyBets: {
      async handler(val: MyBet) {
        if (this.showDetailModal && val && this.selectedBet) {
          this.selectedBet = val;
          await this.checkCashout(val);
          await this.checkCashoutPrematch(val);
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      fetchMyBetsAllWithoutCancelled: 'user/fetchMyBetsAllWithoutCancelled',
      fetchCashout: 'odds/fetchCashout',
      fetchEventResultAndTimeDetail: 'odds/fetchEventResultAndTimeDetail',
      setDetailMyBet: 'user/setDetailMyBet'
    }),
    t(key: string): string {
      return this.$t(key) as string;
    },
    formatDate(date: string | number): string {
      return new Date(date).toLocaleString();
    },
    formatAmount(amount: number | string): string {
      return parseFloat(String(amount)).toFixed(2);
    },
    getWinning(bet: MyBet): string {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return `${bet.currencySymbol} ${this.formatAmount(
            bet._multiple_detail.winning_total
          )}`;
        case 'integral':
          return `${bet.currencySymbol} ${this.formatAmount(
            bet._integral_detail.winning_total_max
          )}`;
        case 'system':
          return `${bet.currencySymbol} ${this.formatAmount(
            bet._system_detail.winning_all_total
          )}`;
        default:
          return `${bet.currencySymbol} 0.00`;
      }
    },
    async initialize(): Promise<void> {
      this.loading = true;
      try {
        await this.searchBets();
        this.calculateValueMyBets(this.myBetsAll);
      } catch (error) {
        console.error('Error initializing bet list:', error);
      } finally {
        this.loading = false;
      }
    },
    async searchBets(): Promise<void> {
      const { fromTime, toTime } = this.getEpochTimes();
      const criteria: Record<string, unknown> = {};

      if (this.filters.type) criteria.type = this.filters.type;
      if (this.filters.result) criteria.result = this.filters.result;
      if (this.filters.payed) criteria.payed = this.filters.payed;
      if (this.filters.code) criteria.code = this.filters.code;
      if (this.filters.betCode) criteria.betCode = this.filters.betCode;
      if (this.filters.bet_context)
        criteria.bet_context = this.filters.bet_context;
      if (fromTime) criteria.fromTime = fromTime;
      if (toTime) criteria.toTime = toTime;

      if (this.showOnlyMyBetsToggle) {
        criteria.onlyPlayedByMe = this.filters.onlyPlayedByMe;
      }

      await this.fetchMyBetsAllWithoutCancelled(criteria);
    },
    getEpochTimes(): { fromTime: number | null; toTime: number | null } {
      const DEFAULT_TIMEZONE = 'Europe/Malta';
      const platformSettings = this.getPlatformSettings;
      const timezone = platformSettings?.timezone || DEFAULT_TIMEZONE;

      const fromTime =
        this.filters.fromTime && this.filters.fromTime.trim() !== ''
          ? this.filters.fromTime
          : moment.tz(timezone).startOf('day').format('YYYY-MM-DD');

      const toTime =
        this.filters.toTime && this.filters.toTime.trim() !== ''
          ? this.filters.toTime
          : moment.tz(timezone).endOf('day').format('YYYY-MM-DD');

      const fromTimeEpoch = moment
        .tz(fromTime, timezone)
        .startOf('day')
        .valueOf();
      const toTimeEpoch = moment.tz(toTime, timezone).endOf('day').valueOf();

      return { fromTime: fromTimeEpoch, toTime: toTimeEpoch };
    },
    calculateValueMyBets(bets: MyBet[]): void {
      const count_bets = bets?.length || 0;

      if (count_bets > 0) {
        let total_played = 0;
        let value_win = 0;
        let value_void = 0;
        let value_pending = 0;
        let currency = '';

        bets.forEach((bet: MyBet) => {
          currency = bet.currencySymbol;
          total_played += Number(bet.amount_real);

          if (bet.result === 'win') {
            value_win += Number(bet.winning_real || 0);
          }

          if (bet.result === 'void') {
            value_void += Number(bet.amount_real);
          }

          if (bet.result === 'pending') {
            switch (bet.type) {
              case 'multiple':
              case 'single':
                value_pending += Number(bet._multiple_detail.winning_total);
                break;
              case 'integral':
                value_pending += Number(bet._integral_detail.winning_total_max);
                break;
              case 'system':
                value_pending += Number(bet._system_detail.winning_all_total);
                break;
            }
          }
        });

        const net = value_win - (total_played - value_void);

        this.betsStats = {
          total: count_bets,
          totalPlayed: total_played,
          winner: value_win,
          void: value_void,
          pending: value_pending,
          net: net,
          currency: currency
        };
      } else {
        this.betsStats = {
          total: 0,
          totalPlayed: 0,
          winner: 0,
          void: 0,
          pending: 0,
          net: 0,
          currency: ''
        };
      }
    },
    async handleSearch(filters: MyBetsFilters): Promise<void> {
      this.filters = { ...filters };
      this.currentPage = 1;
      this.loading = true;
      try {
        await this.searchBets();
        this.calculateValueMyBets(this.myBetsAll);
      } catch (error) {
        console.error('Error searching bets:', error);
      } finally {
        this.loading = false;
      }
    },
    async handleReset(filters: MyBetsFilters): Promise<void> {
      const now = new Date();
      const oneMonthAgo = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        now.getDate()
      );

      this.filters = {
        type: '',
        result: '',
        payed: '',
        code: '',
        betCode: '',
        bet_context: '',
        fromTime: oneMonthAgo.toISOString().substr(0, 10),
        toTime: new Date().toISOString().substr(0, 10),
        onlyPlayedByMe: true
      } as MyBetsFilters;

      this.currentPage = 1;
      this.loading = true;
      try {
        await this.searchBets();
        this.calculateValueMyBets(this.myBetsAll);
      } catch (error) {
        console.error('Error resetting bets:', error);
      } finally {
        this.loading = false;
      }
    },
    nextPage(): void {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage(): void {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    goToPage(page: number): void {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    async openDetail(bet: MyBet): Promise<void> {
      this.detailLoading = true;
      this.selectedBet = bet;
      this.showDetailModal = true;

      try {
        await this.checkCashout(bet);
        await this.checkCashoutPrematch(bet);

        if (bet.result === 'pending') {
          for (let i = 0; i < bet.odds.length; i++) {
            const data = await this.getLiveDate(bet.odds[i].event_source);
            if (data && data.livescore) {
              const time_actual = data.livescore.Scoreboard?.Time || 0;
              const result_actual = data.livescore.Scoreboard?.Results || [];
              bet.odds[i].show_time = true;
              bet.odds[i].time_actual = Number(time_actual) / 60;
              bet.odds[i].result_actual_home = this.getResult(
                result_actual,
                'home'
              );
              bet.odds[i].result_actual_away = this.getResult(
                result_actual,
                'away'
              );
            } else {
              bet.odds[i].show_time = false;
              bet.odds[i].time_actual = '';
              bet.odds[i].result_actual_home = '';
              bet.odds[i].result_actual_away = '';
            }
          }
        }

        this.setDetailMyBet(bet);
      } catch (error) {
        console.error('Error opening bet detail:', error);
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetailModal(): void {
      this.showDetailModal = false;
      this.selectedBet = null;
      this.cashout = 0;
      this.cashoutPrematch = 0;
    },
    handleCashoutSuccess(): void {
      this.closeDetailModal();
      this.initialize();
    },
    handleCashoutError(): void {
      // Handle error if needed
    },
    async getLiveDate(eventSource: number): Promise<{
      livescore?: {
        Scoreboard?: {
          Time?: number;
          Results?: Array<{ Position: string; Value: string }>;
        };
      };
    } | null> {
      try {
        const data = await this.fetchEventResultAndTimeDetail({
          source: eventSource
        });
        return data;
      } catch {
        return null;
      }
    },
    getResult(
      result: Array<{ Position: string; Value: string }>,
      team: string
    ): number | string {
      if (!result || !Array.isArray(result)) return 0;
      const position = team === 'home' ? '1' : '2';
      const point = result.find(r => r.Position === position);
      return point ? +point.Value : 0;
    },
    async checkCashout(bet: MyBet): Promise<void> {
      if (bet.type === 'integral' || bet.type === 'system') {
        this.cashout = 0;
        return;
      }

      if (!this.getCashoutLive) {
        this.cashout = 0;
        return;
      }

      const rules_skin = this.getRulesSkin || [];
      const rules_manager = this.getRulesManager || [];

      let rules_cashout_percentage = rules_skin.find(
        (r: { detail?: { context?: number }; type?: string }) =>
          r.detail?.context === 1 && r.type === 'percentage'
      );
      if (!rules_cashout_percentage) {
        rules_cashout_percentage = rules_manager.find(
          (r: { detail?: { context?: number }; type?: string }) =>
            r.detail?.context === 1 && r.type === 'percentage'
        );
      }

      const percentage_bookmaker =
        rules_cashout_percentage?.detail?.value ?? 0.97;
      const amount_real = Number(bet.amount_real);
      const bet_result = bet.result;
      const applicabile: string[] = [];
      const moltiplicatore: number[] = [];

      for (let i = 0; i < bet.odds.length; i++) {
        const odd = bet.odds[i];
        const id_outcome = odd.id_outcome;
        const odd_value = Number(odd.outcome_odd_value_for_order);
        const unique_id = odd.unique_id;
        const type_event = odd.type;
        const ext_id = odd.ext_id;

        if (odd_value > 7) {
          this.cashout = 0;
          return;
        }

        try {
          const odds_instant = await this.fetchCashout({
            id: odd.event_source,
            outcome_id: id_outcome,
            unique_id: unique_id,
            ext_id: ext_id
          });

          if (odds_instant && odds_instant.length > 0) {
            for (const item of odds_instant) {
              if (
                item.odds &&
                item.odds.outcome?._attributes?.id === id_outcome
              ) {
                if (item.livescore) {
                  bet.odds[i].match_livescore = item.livescore;
                }
                bet.odds[i].sport_event_status =
                  item.sport_event_status?._attributes?.status;

                const odd_value_actual = item.status?.odds || 0;
                const status_odd = item.status?.status;

                const vincita = amount_real * odd_value;
                const probabilita = 1 / odd_value_actual;
                const vincita_nuova =
                  (vincita * probabilita * percentage_bookmaker) / amount_real;

                if (
                  type_event === 'live' &&
                  (status_odd === 1 || status_odd === 3) &&
                  bet_result === 'pending' &&
                  odd_value_actual <= 7
                ) {
                  applicabile.push('true');
                  moltiplicatore.push(vincita_nuova);
                } else {
                  applicabile.push('false');
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking cashout:', error);
          applicabile.push('false');
        }
      }

      if (!applicabile.includes('false') && moltiplicatore.length > 0) {
        const importo_cashout =
          moltiplicatore.reduce((a, b) => a * b, 1) * amount_real;
        this.cashout = importo_cashout > 0 ? Math.abs(importo_cashout) : 0;
      } else {
        this.cashout = 0;
      }
    },
    async checkCashoutPrematch(bet: MyBet): Promise<void> {
      try {
        if (bet.type === 'integral' || bet.type === 'system') {
          this.cashoutPrematch = 0;
          return;
        }

        if (!this.getCashout) {
          this.cashoutPrematch = 0;
          return;
        }

        const rules_skin = this.getRulesSkin || [];
        const rules_manager = this.getRulesManager || [];

        let rules_cashout_percentage = rules_skin.find(
          (r: { detail?: { context?: number }; type?: string }) =>
            r.detail?.context === 0 && r.type === 'percentage'
        );
        if (!rules_cashout_percentage) {
          rules_cashout_percentage = rules_manager.find(
            (r: { detail?: { context?: number }; type?: string }) =>
              r.detail?.context === 0 && r.type === 'percentage'
          );
        }

        const cashoutrisk = rules_cashout_percentage?.detail?.value ?? 20;

        let rules_cashout_min_events = rules_skin.find(
          (r: { detail?: { context?: number }; type?: string }) =>
            r.detail?.context === 0 && r.type === 'min_event'
        );
        if (!rules_cashout_min_events) {
          rules_cashout_min_events = rules_manager.find(
            (r: { detail?: { context?: number }; type?: string }) =>
              r.detail?.context === 0 && r.type === 'min_event'
          );
        }
        const min_events_cashout = rules_cashout_min_events?.detail?.value ?? 2;

        let rules_cashout_min_odds = rules_skin.find(
          (r: { detail?: { context?: number }; type?: string }) =>
            r.detail?.context === 0 && r.type === 'min_odds'
        );
        if (!rules_cashout_min_odds) {
          rules_cashout_min_odds = rules_manager.find(
            (r: { detail?: { context?: number }; type?: string }) =>
              r.detail?.context === 0 && r.type === 'min_odds'
          );
        }
        const min_odds_cashout = rules_cashout_min_odds?.detail?.value ?? 1.2;

        let rules_cashout_max_events = rules_skin.find(
          (r: { detail?: { context?: number }; type?: string }) =>
            r.detail?.context === 0 && r.type === 'max_event'
        );
        if (!rules_cashout_max_events) {
          rules_cashout_max_events = rules_manager.find(
            (r: { detail?: { context?: number }; type?: string }) =>
              r.detail?.context === 0 && r.type === 'max_event'
          );
        }
        const max_events_cashout = rules_cashout_max_events?.detail?.value ?? 7;

        const nr_events = bet.odds.length;
        const perc_events = 100 / nr_events;
        const value_percent_cashout =
          100 - (perc_events - (perc_events * cashoutrisk) / 100);
        const amount_real = Number(bet.amount_real);
        const bet_result = bet.result;
        const bet_context = bet.bet_context;
        const in_checkout: number[] = [];

        if (bet_context === 'prematch' && bet_result === 'pending') {
          const date_now = new Date();
          let evento_in_corso = false;
          const count_events = bet.odds.length;

          if (
            count_events >= min_events_cashout &&
            count_events <= max_events_cashout
          ) {
            for (let i = 0; i < count_events; i++) {
              const odd = bet.odds[i];
              const event_defined = odd.defined ? true : false;
              let odd_value = Number(odd.outcome_odd_value_for_order);
              const event_result = odd.result ? true : false;
              const event_half_won = odd.half_won || false;
              const event_time = odd.time;
              const status_event = odd.status_event;

              if (odd_value < min_odds_cashout) {
                this.cashoutPrematch = 0;
                return;
              }

              if (status_event !== 1) {
                this.cashoutPrematch = 0;
                return;
              }

              if (event_defined && (event_result || event_half_won)) {
                if (event_half_won) {
                  odd_value = odd_value / 2;
                }
                in_checkout.push(odd_value);
              } else if (!event_defined && event_time < date_now.getTime()) {
                evento_in_corso = true;
              }
            }
          }

          if (in_checkout.length > 0 && !evento_in_corso) {
            let quota = 1;
            for (const val of in_checkout) {
              quota *= val;
            }

            const vincita = amount_real * quota;
            const profitto = vincita - amount_real;
            const valore_cashout = (profitto * value_percent_cashout) / 100;
            const importo_cashout = profitto - Math.abs(valore_cashout);
            const real_cashout = importo_cashout + amount_real;

            this.cashoutPrematch =
              real_cashout > 0 ? Math.abs(real_cashout) : 0;
          } else {
            this.cashoutPrematch = 0;
          }
        } else {
          this.cashoutPrematch = 0;
        }
      } catch (e) {
        console.error('Error checking cashout prematch:', e);
        this.cashoutPrematch = 0;
      }
    }
  }
});
